import { createSelector } from 'reselect'

import { removeDupes } from 'utils/helpers';
import { testUserAgainstString } from 'utils/user';

const sortByName = (users, currentUserId) =>
    users.sort((u1, u2) => {
        if (u1.id === currentUserId) {
            return -1
        }

        if (u2.id === currentUserId) {
            return 1;
        }

        const u1Name = (u1.name || u1.email || '').toLowerCase();
        const u2Name = (u2.name || u2.email || '').toLowerCase();

        if (u1Name > u2Name) return 1;
        if (u1Name < u2Name) return -1;

        return 0;
    });

const resolveUsers = ({ userIds, users }) => users ? (userIds || []).map(user => users.items.find(u => u && user && u.id === (user.id || user))).filter(Boolean) : [];

const getUser = (users, userId) => users?.items?.find(user => user.id === userId);
export const makeGetUser = () => createSelector(getUser, user => user);

const getUsers = (users, userIds) => users?.filter(user => userIds.includes(user.id));
export const makeGetUsers = () => createSelector(getUsers, users => users);

const getCurrentUser = users => users?.currentUserId && users.items.find(user => user.id === users.currentUserId);
export const makeGetCurrentUser = () => createSelector(getCurrentUser, user => user);

const getCurrentUserId = users => users.currentUserId;
export const makeGetCurrentUserId = () => createSelector(getCurrentUserId, userId => userId);

const getCurrentMasterUserId = users => users.currentUserId && users.items.find(user => user.id === users.currentUserId)?.user_id;
export const makeGetCurrentMasterUserId = () => createSelector(getCurrentMasterUserId, userId => userId);

const getResolvedOnlineUsers = (userIds, users) => getResolvedUsers(userIds, users)
    .filter(user => user.online);
export const makeGetResolvedOnlineUsers = () => createSelector(getResolvedOnlineUsers, users => users);

const getResolvedUsers = (userIds, users) => {
    const resolvedUsers = resolveUsers({
        userIds,
        users
    });

    if (Array.isArray(resolvedUsers)) {
        return resolvedUsers;
    }

    return [];
}
export const makeGetResolvedUsers = () => createSelector(getResolvedUsers, users => users);

// Team Users
const getNonGuestTeamUsers = (team, users, excludedUsers) => resolveUsers({
    userIds: getTeamUsers(team, excludedUsers),
    users,
    forceArray: true
}).filter(user => user.role && user.role !== 'client').map(user => user.id);

const getGuestTeamUsers = (team, users, excludedUsers) => resolveUsers({
    userIds: getTeamUsers(team, excludedUsers),
    users,
    forceArray: true
}).filter(user => user.role && user.role === 'client').map(user => user.id);

const getManagersAndOwnerTeamUsers = (team, users, excludedUsers) => resolveUsers({
    userIds: getTeamUsers(team, excludedUsers),
    users,
    forceArray: true
}).filter(user => user.role && ['owner', 'super admin', 'manager'].includes(user.role));

const getNonGuestTeamUsersWithoutMe = (team, meId, users) => getNonGuestTeamUsers(team, users).filter(userId => userId !== meId);

const getTeamUsers = (team, excludedUsers = []) => team?.members ? [team.owner, ...team.members].filter(userId => !excludedUsers.includes(userId)) : [];
export const makeGetTeamUsers = () => createSelector(getTeamUsers, users => users);

const getUsersByRoles = (users, roles) => users.filter(({ role }) => roles.includes(role));
export const makeGetUsersByRoles = () => createSelector(getUsersByRoles, users => users);

const getResolvedTeamUsers = (team, users) => getResolvedUsers(getTeamUsers(team), users);
export const makeGetResolvedTeamUsers = () => createSelector(getResolvedTeamUsers, users => users);

const getOwner = team => team?.owner;
export const makeGetOwner = () => createSelector(getOwner, users => users);

const getResolvedOwner = (team, users) => getResolvedUsers([getOwner(team)], users)[0];
export const makeGetResolvedOwner = () => createSelector(getResolvedOwner, users => users);

const getResolvedNonGuestTeamUsers = (team, users, excludedUsers, currentUserId) => sortByName(removeDupes(getResolvedUsers(getNonGuestTeamUsers(team, users, excludedUsers), users)), users.currentUserId);
export const makeGetResolvedNonGuestTeamUsers = () => createSelector(getResolvedNonGuestTeamUsers, users => users);

const getResolvedGuestTeamUsers = (team, users, excludedUsers) => sortByName(getResolvedUsers(getGuestTeamUsers(team, users, excludedUsers), users), users.currentUserId);
export const makeGetResolvedGuestTeamUsers = () => createSelector(getResolvedGuestTeamUsers, users => users);

const getResolvedActiveNonGuestTeamUsers = (team, users) => getResolvedUsers(getNonGuestTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed);
export const makeGetResolvedActiveNonGuestTeamUsers = () => createSelector(getResolvedActiveNonGuestTeamUsers, users => users);

const getResolvedSortedActiveNonGuestTeamUsers = (team, users) => getResolvedUsers(getNonGuestTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed);
export const makeGetResolvedSortedActiveNonGuestTeamUsers = () => createSelector(getResolvedSortedActiveNonGuestTeamUsers, users => users);

const getResolvedActiveGuestTeamUsers = (team, users) => getResolvedUsers(getGuestTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed);
export const makeGetResolvedActiveGuestTeamUsers = () => createSelector(getResolvedActiveGuestTeamUsers, users => users);

const getResolvedActiveNonGuestTeamUsersByTerm = (team, users, projects, labels, term) => sortByName(getResolvedUsers(getNonGuestTeamUsers(team, users), users).filter(user => {
        if (!projects.some(project => project?.members?.includes(user.id) || project?.owner === user.id)) {
            return false;
        }

        if (user.pending || user.loading || user.removed) {
            return false;
        }

        return testUserAgainstString(user, term, labels);
    }), users.currentUserId);
export const makeGetResolvedActiveNonGuestTeamUsersByTerm = () => createSelector(getResolvedActiveNonGuestTeamUsersByTerm, users => users);

const getSortedResolvedAndActiveNonGuestTeamUsers = (team, users) => getResolvedUsers(getNonGuestTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed)
    .sort((u1, u2) => (u1.name || u1.email || '').toLowerCase() > (u2.name || u2.email || '').toLowerCase() ? 1 : -1);
export const makeGetSortedResolvedAndActiveNonGuestTeamUsers = () => createSelector(getSortedResolvedAndActiveNonGuestTeamUsers, users => users);

const getSortedResolvedAndActiveManagersAndOwner = (team, users) => getResolvedUsers(getManagersAndOwnerTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed)
    .sort((u1, u2) => (u1.name || u1.email || '').toLowerCase() > (u2.name || u2.email || '').toLowerCase() ? 1 : -1);
export const makeGetSortedResolvedAndActiveManagersAndOwner = () => createSelector(getSortedResolvedAndActiveManagersAndOwner, users => users);

const getSortedResolvedAndActiveGuestTeamUsers = (team, users) => getResolvedUsers(getGuestTeamUsers(team, users), users)
    .filter(user => !user.pending && !user.loading && !user.removed)
    .sort((u1, u2) => (u1.name || u1.email || '').toLowerCase() > (u2.name || u2.email || '').toLowerCase() ? 1 : -1);
export const makeGetSortedResolvedAndActiveGuestTeamUsers = () => createSelector(getSortedResolvedAndActiveGuestTeamUsers, users => users);

const getSortedResolvedAndActiveNonGuestTeamUsersWithoutMe = (team, meId, users) =>
    getResolvedUsers(getNonGuestTeamUsersWithoutMe(team, meId, users), users)
        .filter(user => !user.pending && !user.loading && !user.removed)
        .sort((u1, u2) => (u1.name || u1.email || '').toLowerCase() > (u2.name || u2.email || '').toLowerCase() ? 1 : -1);
export const makeGetSortedResolvedAndActiveNonGuestTeamUsersWithoutMe = () => createSelector(getSortedResolvedAndActiveNonGuestTeamUsersWithoutMe, users => users);

const hasConversationForUser = (discussions, userId) => discussions
    .some(conversation =>
        conversation.type === 'direct' &&
        conversation.members.some(user => user.id === userId)
    );

const getSortedAndFilteredUsersWithoutConversations = (team, users, discussions, searchTerm = '', maxCount, withGuests) =>
    (withGuests ? getResolvedTeamUsers(team, users) : getResolvedNonGuestTeamUsers(team, users))
    .filter(user =>
        !user.pending &&
        !user.removed &&
        !hasConversationForUser(discussions, user.id) &&
        `${user.email}&&${user.name}`.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
    )
    .sort((u1, u2) => (u1.name || u1.email || '').toLowerCase() > (u2.name || u2.email || '').toLowerCase() ? 1 : -1)
    .slice(0, maxCount);
export const makeGetSortedAndFilteredUsersWithoutConversations = () => createSelector(getSortedAndFilteredUsersWithoutConversations, users => users);

export const makeGetNonGuestTeamUsers = () => createSelector(getNonGuestTeamUsers, users => users);

export const makeGetGuestTeamUsers = () => createSelector(getGuestTeamUsers, users => users);


// Project Users
const getResolvedNonGuestProjectUsers = (project, users) => resolveUsers({
    userIds: getProjectUsers(project),
    users,
    forceArray: true
}).filter(user => user.role && user.role !== 'client');
const getResolvedGuestProjectUsers = (project, users) => resolveUsers({
    userIds: getProjectUsers(project),
    users,
    forceArray: true
}).filter(user => user.role && user.role === 'client');

const getProjectUsers = (project, excludedUsers = []) => [project?.owner, ...(project?.members || [])].filter(userId => userId && !excludedUsers.includes(userId));
export const makeGetProjectUsers = () => createSelector(getProjectUsers, users => users);

const getResolvedProjectUsers = (project, users, excludedUsers) => getResolvedUsers(getProjectUsers(project, excludedUsers), users);
export const makeGetResolvedProjectUsers = () => createSelector(getResolvedProjectUsers, users => users);

const getResolvedSortedProjectUsers = (project, users, excludedUsers) => sortByName(getResolvedUsers(getProjectUsers(project, excludedUsers), users));
export const makeGetResolvedSortedProjectUsers = () => createSelector(getResolvedSortedProjectUsers, users => users);

export const makeGetResolvedNonGuestProjectUsers = () => createSelector(getResolvedNonGuestProjectUsers, users => users);

export const makeGetResolvedGuestProjectUsers = () => createSelector(getResolvedGuestProjectUsers, users => users);

const getResolvedDocUsers = (doc, users) => getResolvedUsers((doc && doc.members && doc.members.map(u => u.id)) || [], users);
export const makeGetResolvedDocUsers = () => createSelector(getResolvedDocUsers, users => users);

const getUsersFilteredByLabel = (labelId, users) => users.filter(user => user.labels.includes(labelId));
export const makeGetUsersFilteredByLabel = () => createSelector(getUsersFilteredByLabel, users => users);
